import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useCart } from "@/contexts/CartContext";
import { useFavourites } from "@/contexts/FavouritesContext";
import { useSiteConfig } from "@/contexts/SiteConfigContext";
import { brandAssetUrl } from "@/lib/api";
import { Heart } from "lucide-react";

const linkClass = ({ isActive }) =>
  `text-xs uppercase tracking-[0.25em] font-semibold transition-colors ${
    isActive ? "text-[var(--pm-orange)]" : "text-white/70 hover:text-[var(--pm-orange)]"
  }`;

export default function Navigation() {
  const { user, logout } = useAuth();
  const { count: cartCount } = useCart();
  const { count: favCount } = useFavourites();
  const site = useSiteConfig();
  const navigate = useNavigate();
  const isPhotographer = user && (user.role === "photographer");
  const isSuper = user && (user.role === "admin" || user.role === "super_admin");
  const showCart = !isPhotographer && !isSuper;

  return (
    <header
      className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-black/60 border-b-2 border-[var(--pm-orange-30)]"
      data-testid="site-header"
    >
      <nav className="max-w-[1600px] mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <Link
          to="/"
          className="flex items-center gap-3 hover:opacity-90 transition shrink-0"
          data-testid="brand-logo"
        >
          {site.has_nav_logo ? (
            <img
              src={brandAssetUrl("/api/public/branding/nav_logo", site.branding_version)}
              alt={site.siteName}
              className="h-9 sm:h-11 md:h-12 w-auto object-contain"
              data-testid="nav-custom-logo"
            />
          ) : site.preset_key === "photomoto" ? (
            <img
              src="/brand/photomoto-icon-light.png"
              alt={site.siteName}
              className="h-9 sm:h-11 md:h-12 w-auto"
            />
          ) : null}
          <div className="hidden sm:block leading-none">
            <p className="font-display text-2xl md:text-3xl uppercase tracking-tight text-white">
              {site.site_name_primary}<span className="pm-orange">{site.site_name_accent}</span>
            </p>
            <p className="font-condensed text-[10px] md:text-xs uppercase tracking-[0.35em] text-white/50 mt-0.5">
              .{site.site_domain.split(".").slice(1).join(".")}
            </p>
          </div>
        </Link>

        <div className="flex items-center gap-3 sm:gap-6 md:gap-8 min-w-0">
          <NavLink to="/events" className={linkClass} data-testid="nav-gallery">
            Events
          </NavLink>

          {isSuper && (
            <NavLink to="/admin" className={linkClass} data-testid="nav-admin">
              Admin
            </NavLink>
          )}

          {(isPhotographer || isSuper) && (
            <NavLink to="/studio" className={linkClass} data-testid="nav-studio">
              Studio
            </NavLink>
          )}

          {user && !isPhotographer && !isSuper && (
            <NavLink to="/my-purchases" className={linkClass} data-testid="nav-purchases">
              My Photos
            </NavLink>
          )}

          {showCart && (
            <NavLink
              to="/favourites"
              className={({ isActive }) =>
                `relative flex items-center text-xs uppercase tracking-[0.25em] font-semibold transition-colors ${
                  isActive ? "text-[var(--pm-orange)]" : "text-white/70 hover:text-[var(--pm-orange)]"
                }`
              }
              data-testid="nav-favourites"
              aria-label="Favourites"
            >
              <Heart size={19} fill={favCount > 0 ? "currentColor" : "none"} />
              {favCount > 0 && (
                <span
                  className="absolute -top-2 -right-3 min-w-[20px] h-5 px-1 bg-[var(--pm-orange)] text-black text-[11px] font-bold flex items-center justify-center rounded-full leading-none"
                  data-testid="favourites-badge"
                >
                  {favCount}
                </span>
              )}
            </NavLink>
          )}

          {showCart && (
            <NavLink
              to="/cart"
              className={({ isActive }) =>
                `relative flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-semibold transition-colors ${
                  isActive ? "text-[var(--pm-orange)]" : "text-white/70 hover:text-[var(--pm-orange)]"
                }`
              }
              data-testid="nav-cart"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="9" cy="21" r="1"></circle>
                <circle cx="20" cy="21" r="1"></circle>
                <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
              </svg>
              <span className="hidden md:inline">Basket</span>
              {cartCount > 0 && (
                <span
                  className="absolute -top-2 -right-3 min-w-[20px] h-5 px-1 bg-[var(--pm-orange)] text-black text-[11px] font-bold flex items-center justify-center rounded-full leading-none"
                  data-testid="cart-badge"
                >
                  {cartCount}
                </span>
              )}
            </NavLink>
          )}

          {!user ? (
            <>
              <Link
                to="/photographers"
                className="text-xs uppercase tracking-[0.2em] font-semibold text-[var(--pm-orange)] hover:text-[var(--pm-orange-bright)]"
                data-testid="nav-photographers"
              >
                For photographers
              </Link>
              <Link
                to="/login"
                className="text-xs uppercase tracking-[0.2em] font-semibold text-white/80 hover:text-white"
                data-testid="nav-login"
              >
                Sign in
              </Link>
            </>
          ) : (
            <button
              onClick={async () => { await logout(); navigate("/"); }}
              className="text-xs uppercase tracking-[0.2em] font-semibold text-white/70 hover:text-white"
              data-testid="nav-logout"
            >
              Sign out
            </button>
          )}
        </div>
      </nav>
    </header>
  );
}
